import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../modules/auth/core/Auth";
import { showErrorToast, showSuccessToast } from "../utils/toast";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

export type TestFlagKind = "students" | "assessments" | "institutes";

/**
 * Marks or unmarks a record as a test record (QA fix plan §2.7). Super-admin only on the
 * server; the body must be a JSON boolean.
 */
export function setTestFlag(kind: TestFlagKind, id: number, isTest: boolean) {
  return axios.put<{ isTest: boolean }>(`${API_URL}/test-flags/${kind}/${id}`, { isTest });
}

const NOUN: Record<TestFlagKind, string> = {
  students: "student",
  assessments: "assessment",
  institutes: "institute",
};

/** What leaving a record out means, said before the click rather than after. */
const EFFECT: Record<TestFlagKind, string> = {
  students:
    "They will be left out of every dashboard count and Live Tracking. The Report Center marks their rows \"Test\" and leaves them out of Send All, the ZIP and Generate unless ticked.",
  assessments:
    "It will be left out of every dashboard count, the dashboard pickers and Live Tracking, at every school. The Report Center marks its rows \"Test\" and leaves them out of Send All, the ZIP and Generate unless ticked.",
  institutes:
    "Everyone at it will be left out of every dashboard count and Live Tracking. The Report Center marks their rows \"Test\" and leaves them out of Send All, the ZIP and Generate unless ticked.",
};

/**
 * The test-record flag on a row of an admin list: a "Test" badge while the record is flagged,
 * a quiet "Mark as test" otherwise. Only a super-admin sees it at all, since the flag hides a
 * record from every school's numbers; everyone else gets nothing.
 */
const TestFlagToggle: React.FC<{
  kind: TestFlagKind;
  id: number | null | undefined;
  isTest: boolean;
  /** The record's name, for the confirmation. */
  name?: string;
  onChange: (isTest: boolean) => void;
}> = ({ kind, id, isTest, name, onChange }) => {
  const { currentUser } = useAuth();
  const [saving, setSaving] = useState(false);
  if (currentUser?.superAdmin !== true || id == null) return null;

  const noun = NOUN[kind];
  const toggle = async (e: React.MouseEvent) => {
    // Rows in these lists open or select on click; the flag is its own action.
    e.stopPropagation();
    const next = !isTest;
    const who = name ? `"${name}"` : `this ${noun}`;
    const question = next
      ? `Mark ${who} as a test record?\n\n${EFFECT[kind]}`
      : `Unmark ${who}? It will be counted again everywhere.`;
    if (!window.confirm(question)) return;
    setSaving(true);
    try {
      await setTestFlag(kind, id, next);
      onChange(next);
      showSuccessToast(next ? `Marked ${who} as a test record.` : `${who} is counted again.`);
    } catch (err: any) {
      showErrorToast(err?.response?.data?.error || err?.response?.data?.message || `Could not change the ${noun}.`);
    } finally {
      setSaving(false);
    }
  };

  return isTest ? (
    <button
      type="button"
      className="badge badge-light-warning border-0"
      style={{ cursor: saving ? "wait" : "pointer", fontSize: "0.72rem" }}
      disabled={saving}
      onClick={toggle}
      title={`Test record: left out of the dashboards and Live Tracking. Click to unmark this ${noun}.`}
    >
      {saving ? "…" : "Test"}
    </button>
  ) : (
    <button
      type="button"
      className="btn btn-link btn-sm p-0 text-muted"
      style={{ fontSize: "0.72rem", textDecoration: "none" }}
      disabled={saving}
      onClick={toggle}
      title={`Mark this ${noun} as a test record`}
    >
      {saving ? "…" : "Mark as test"}
    </button>
  );
};

export default TestFlagToggle;
